// 261010 P8b-1 — tầng 2 chi tiết video + drawer Lịch sử (dời nguyên JSX từ FilmWorkflowPage). Không thêm tính năng.
import React from 'react';
import { Alert, Button, Drawer, Spin } from 'antd';
import type { EventPlan, FilmEvent, FilmRunningJob, PositionMapsResponse, StoryboardListItem } from '../../types/film';
import type { NavigateFunction } from 'react-router-dom';
import type { WorkflowStatus, WorkflowStepKey } from '../../types/filmWorkflow';
import type { PromptPick } from '../../types/filmStepReview';
import WorkflowStepContent from './WorkflowStepContent';
import WorkflowStepReviewCard from './WorkflowStepReviewCard';
import Chip from './WorkflowChip';
import {
  STATE_META, WORKFLOW_STEPS, alertsForEvent, eventDots, jobsForEvent, neighborEvents, stateOfEvent, stepNameOf, truncateText, workflowPath,
} from './workflowBoardUtils';
import { planStepAlertsOf } from './workflowBellPolicy';
import { approvalNoteOf, stepStateNote } from './workflowDetailUtils';
import { reviewKeyOfStep } from './stepReviewPolicy';
import { actingFlagsOf, isActingFlagAlert } from './actingPolicy';
import { assemblyClipWarnings, clipBoardOf, clipLabelOf, clipOrdersOf, effectiveFocusClipId } from './clipReviewPolicy';
import type { StepReviewsController } from './useStepReviews';

interface Props {
  top: React.ReactNode;
  storyId: string;
  planId: string;
  eventId: string;
  stepParam: WorkflowStepKey | null;
  status: WorkflowStatus;
  detail: EventPlan | null;
  detailError: string | null;
  detailLoading: boolean;
  storyboards: StoryboardListItem[] | null;
  storyboardsError: string | null;
  maps: PositionMapsResponse | null;
  mapsError: string | null;
  eventsById: Map<string, { ev: FilmEvent; sceneId: string }>;
  historyOpen: boolean;
  setHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  promptPick: PromptPick | null;
  setPromptPick: React.Dispatch<React.SetStateAction<PromptPick | null>>;
  clipFocus: string | null;
  setClipFocus: React.Dispatch<React.SetStateAction<string | null>>;
  clipCtl: StepReviewsController;
  guard: (go: () => void) => void;
  navigate: NavigateFunction;
  onOpenEvent: (id: string, step?: WorkflowStepKey | null) => void;
  onBackToBoard: () => void;
  onLoadDetail: () => void;
  onActionDone: () => void;
  onJobsRefresh: () => Promise<{ jobs: FilmRunningJob[]; failures: WorkflowStatus['failures'] } | undefined>;
  setEditDirty: (d: boolean) => void;
}

const WorkflowEventDetail: React.FC<Props> = ({
  top, storyId, planId, eventId, stepParam, status, detail, detailError, detailLoading,
  storyboards, storyboardsError, maps, mapsError, eventsById, historyOpen, setHistoryOpen,
  promptPick, setPromptPick, clipFocus, setClipFocus, clipCtl,
  guard, navigate, onOpenEvent, onBackToBoard, onLoadDetail, onActionDone, onJobsRefresh,
  setEditDirty,
}) => {
  const planSteps = status.steps;
  const sortedEvents = [...status.events].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder);
  const cur = sortedEvents.find((e) => e.eventId === eventId)!;
  const step: WorkflowStepKey = stepParam ?? 'SCRIPT';
  const stState = stateOfEvent(cur, step, planSteps);
  const meta = eventsById.get(eventId);
  const { prev, next } = neighborEvents(sortedEvents, eventId);
  const evAlerts = alertsForEvent(status.alerts, eventId, false);
  const actingFlags = actingFlagsOf(status.alerts, eventId);
  const stepAlerts = evAlerts.filter((a) => a.stepKey === step && !(step === 'DIRECTING' && isActingFlagAlert(a)));
  const planAlerts = planStepAlertsOf(status.alerts, step);
  const hist = jobsForEvent(status.jobs, status.failures, eventId);
  const scene = detail?.scenes.find((s) => s.id === cur.sceneId) ?? null;
  const dots = eventDots(cur, planSteps);
  const reviewKey = reviewKeyOfStep(step);
  const clips = meta?.ev.clips ?? [];
  const focusClipId = effectiveFocusClipId(clips, meta?.ev.selectedClipId, clipFocus);
  const focusOrder = focusClipId ? clipOrdersOf(clips, clipBoardOf(clipCtl.view, eventId))[focusClipId] : undefined;
  const setStep = (k: WorkflowStepKey) => { if (k !== step) guard(() => navigate(workflowPath(storyId, planId, { eventId, step: k }), { replace: true })); };
  return (
    <div className="wf">{top}
      <div className="wf-dtop">
        <button type="button" className="wf-btn" onClick={onBackToBoard}>← Về bảng</button>
        <div className="ttl">
          <h1>Video {cur.eventOrder} <Chip state={stState} /></h1>
          <div className="sub">Cảnh {cur.sceneOrder} · {cur.sceneHeading || `Cảnh ${cur.sceneOrder}`}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" className="wf-btn" disabled={!prev} onClick={() => prev && onOpenEvent(prev.eventId, step)}>‹ Video {prev?.eventOrder ?? ''}</button>
          <button type="button" className="wf-btn" disabled={!next} onClick={() => next && onOpenEvent(next.eventId, step)}>Video {next?.eventOrder ?? ''} ›</button>
          <button type="button" className="wf-btn" onClick={() => setHistoryOpen(true)}>Lịch sử{hist.running.length + hist.failed.length ? ` · ${hist.running.length + hist.failed.length}` : ''}</button>
        </div>
      </div>
      {detailError && <Alert type="warning" showIcon style={{ marginBottom: 10 }} message={detailError} action={<Button size="small" onClick={() => onLoadDetail()}>Tải lại</Button>} />}
      <nav className="wf-dsteps" aria-label={`8 bước của Video ${cur.eventOrder}`}>
        {dots.map((d) => (
          <button key={d.key} type="button" className={`wf-ds${d.key === step ? ' on' : ''}`} onClick={() => setStep(d.key)} aria-current={d.key === step ? 'step' : undefined} aria-label={d.label}>
            <span className="no"><span className={`wf-dot ${d.tone}`} aria-hidden="true" />{d.no}</span>
            <span className="nm">{d.name}</span>
            <span className="st">{STATE_META[d.state].label}</span>
          </button>))}
      </nav>
      <main>
        {reviewKey === 'CLIP_REVIEW' ? (
          meta ? <WorkflowStepReviewCard key={`${eventId}:${step}`} planId={planId} eventId={eventId} stepKey={reviewKey} jobs={status.jobs} onActionDone={onActionDone}
            candidateClipId={focusClipId} clipLabel={focusOrder ? clipLabelOf(focusOrder) : undefined} controller={clipCtl} /> : null
        ) : reviewKey ? (
          <WorkflowStepReviewCard key={`${eventId}:${step}`} planId={planId} eventId={eventId} stepKey={reviewKey} jobs={status.jobs} onActionDone={onActionDone}
            onPickEvidence={step === 'PROMPT' ? setPromptPick : undefined} />
        ) : (
          <div className="wf-review" role="note">
            <b>Phiếu AI chấm</b><span className="muted">Chưa có ở bản này — hệ thống chưa chấm tự động cho bước nào, nên không hiện đạt/hỏng giả.</span>
          </div>
        )}
        <section className="wf-sec" aria-labelledby="wf-step-h">
          <h2 id="wf-step-h">{WORKFLOW_STEPS.find((s) => s.key === step)?.no}. {stepNameOf(step)}</h2>
          <div className="muted" style={{ marginBottom: 6 }}>{WORKFLOW_STEPS.find((s) => s.key === step)?.desc}</div>
          <div style={{ margin: '6px 0 10px' }}><Chip state={stState} /> <span className="muted" style={{ marginLeft: 6 }}>{stepStateNote(stState)}{approvalNoteOf(step) ? ` ${approvalNoteOf(step)}` : ''}</span></div>
          {[...stepAlerts, ...planAlerts].map((a, i) => <div key={i} className={`wf-alert ${a.severity}`}>{a.message}</div>)}
          {detailLoading && !detail && step !== 'ASSEMBLY' ? <div style={{ padding: 12 }}><Spin size="small" /> <span className="muted">Đang tải chi tiết video…</span></div> : <WorkflowStepContent
            step={step} eventId={eventId} ev={meta?.ev ?? null} scene={scene} detail={detail}
            storyboards={storyboards} maps={maps} storyboardsError={storyboardsError} mapsError={mapsError}
            interactive={{
              planId, storyId, stepState: stState, jobs: status.jobs, failures: status.failures, onActionDone, onJobsRefresh,
              events: sortedEvents, onOpenEvent: (id) => onOpenEvent(id, 'VIDEO'),
              setEditDirty, onGoStep: setStep, actingFlags, actingOutcomes: status.workflowRuns?.stage2?.actingOutcomes,
              promptPick: step === 'PROMPT' ? promptPick : null, onClosePromptPick: () => setPromptPick(null),
              clipReviews: step === 'VIDEO' ? { ctl: clipCtl, focusClipId, onFocusClip: setClipFocus } : undefined,
              assemblyClipWarnings: step === 'ASSEMBLY' ? assemblyClipWarnings(status.events, detail ? detail.scenes.flatMap((s) => s.events) : null) : undefined,
            }}
          />}
        </section>
      </main>
      <Drawer title={`Lịch sử · Video ${cur.eventOrder}`} open={historyOpen} onClose={() => setHistoryOpen(false)} width={440} destroyOnClose>
        <div className="wf">
          <h3 className="muted" style={{ margin: '0 0 6px' }}>Đang chạy</h3>
          {hist.running.length === 0 ? <p className="muted">Không có việc nền nào đang chạy.</p> : (
            <ul className="wf-list">{hist.running.map((j) => <li key={j.id}><b>{j.type.replace('FILM_', '')}</b> · {j.runningMinutes}′ / tối đa {j.maxMinutes}′{j.phase ? ` · ${j.phase}` : ''}</li>)}</ul>)}
          <h3 className="muted" style={{ margin: '14px 0 6px' }}>Lỗi gần đây</h3>
          {hist.failed.length === 0 ? <p className="muted">Không có việc nào lỗi trong vài giờ qua.</p> : (
            <ul className="wf-list">{hist.failed.map((f) => <li key={f.id}><b>{f.type.replace('FILM_', '')}</b> · {f.minutesAgo}′ trước<div className="muted">{truncateText(f.note, 400)}</div></li>)}</ul>)}
          <h3 className="muted" style={{ margin: '14px 0 6px' }}>Cảnh báo của video này</h3>
          {evAlerts.length === 0 ? <p className="muted">Không có.</p> : evAlerts.map((a, i) => <div key={i} className={`wf-alert ${a.severity}`}><b>{stepNameOf(a.stepKey)}</b> · {a.message}</div>)}
          <p className="faint" style={{ fontSize: 12 }}>Lịch sử theo phiên bản của từng bước chưa có ở bản này.</p>
        </div>
      </Drawer>
    </div>
  );
};

export default WorkflowEventDetail;
